import { type FieldMetadata, useInputControl } from "@conform-to/react"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import * as React from "react"

import { cn } from "#app/utils/misc.tsx"

export type CheckboxProps = Omit<
	React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
	"type"
> & {
	type?: string
}

const Checkbox = ({
	className,
	meta,
	...props
}: Omit<
	React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
	"type"
> & {
	type?: string
	meta: FieldMetadata<string | boolean | undefined>
}) => {
	const ref =
		React.useRef<React.ElementRef<typeof CheckboxPrimitive.Root>>(null)
	const control = useInputControl(meta)

	return (
		<>
			<input
				className="sr-only"
				name={meta.name}
				tabIndex={-1}
				defaultValue={meta.initialValue}
				onFocus={() => ref.current?.focus()}
			/>
			<CheckboxPrimitive.Root
				ref={ref}
				className={cn(
					"peer h-4 w-4 shrink-0 rounded-sm border border-primary shadow focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground",
					className,
				)}
				checked={control.value === "on"}
				onCheckedChange={checked => {
					control.change(checked ? "on" : "")
				}}
				onBlur={control.blur}
				{...props}
				id={meta.id}
				type="button"
			>
				<CheckboxPrimitive.Indicator
					className={cn("flex items-center justify-center text-current")}
				>
					<svg viewBox="0 0 8 8">
						<path
							d="M1,4 L3,6 L7,2"
							stroke="currentcolor"
							strokeWidth="1"
							fill="none"
						/>
					</svg>
				</CheckboxPrimitive.Indicator>
			</CheckboxPrimitive.Root>
		</>
	)
}
Checkbox.displayName = CheckboxPrimitive.Root.displayName

export { Checkbox }
